import { useState, type FormEvent } from 'react';
import { REGISTRATION_NAME_RE, type Extension } from '@arcnet/contracts';
import { useRegistrations, useRegistrationActions } from '../../lib/api/hooks-extension.js';
import { useSession } from '../../lib/auth/session.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { Switch } from '../../components/ui/Form.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { toastError } from '../../components/ui/Toast.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';

/**
 * Up to two more logins on this extension, named by whoever adds them -- luke-meridian -- for a
 * softphone or a second app (contracts registrations.ts). Each has its own password and its own
 * switch for whether inbound calls ring it. A name already on the account is refused.
 */
export function AdditionalRegistrations({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const can = useSession((s) => s.can);
  const me = useSession((s) => s.session?.user.id);
  const regs = useRegistrations(tenantId, ext.id);
  const act = useRegistrationActions(tenantId, ext.id);
  const step = useStepUp();
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [shown, setShown] = useState<{ username: string; password: string } | null>(null);
  const mayWrite = can('extension:write');
  const mayRing = mayWrite || (can('extension:write:own') && ext.user_id === me);
  const data = regs.data;
  const list = data?.additional ?? [];
  const full = !!data && list.length >= data.max;
  const clean = name.trim().toLowerCase();
  const valid = REGISTRATION_NAME_RE.test(clean);

  const add = (e: FormEvent) => {
    e.preventDefault();
    act.add.mutate(clean, {
      onSuccess: (r) => {
        setShown({ username: r.username, password: r.sip_credential.password });
        setName(''); setAdding(false);
      },
    });
  };

  const newPassword = (id: string, username: string) => {
    if (!confirm(`Issue ${username} a new password? Whatever signs in with it now stops until it has the new one.`)) return;
    step.run(async () => {
      const r = await act.password.mutateAsync(id);
      setShown({ username, password: r.sip_credential.password });
      return r;
    }).catch(toastError);
  };

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <div className="arc-card-head">
        <h2 className="arc-h2">Additional registrations</h2>
        {mayWrite && data && !full && !adding && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setAdding(true)}>
            <Icon name="plus" size={12} /> Add registration
          </button>
        )}
      </div>
      <div className="arc-card-body">
      <StepUpPrompt step={step} what="Issuing a new password" />
      {act.add.error && <ErrorBanner error={act.add.error} />}
      {act.remove.error && <ErrorBanner error={act.remove.error} />}
      {act.ring.error && <ErrorBanner error={act.ring.error} />}

      {shown && (
        <div className="arc-row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12,
          padding: '8px 10px', border: '1px solid var(--arc-border)', borderRadius: 'var(--arc-r-sm)' }}>
          <span className="arc-mono">{shown.username}</span>
          <span className="arc-mono" style={{ fontWeight: 600 }}>{shown.password}</span>
          <span className="arc-subtle" style={{ fontSize: 12 }}>Shown once. Copy it now.</span>
          <div className="arc-spacer" />
          <button type="button" className="arc-btn arc-btn-sm"
            onClick={() => { void navigator.clipboard?.writeText(shown.password); }}>Copy</button>
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setShown(null)}>Done</button>
        </div>
      )}

      {list.length > 0 && (
        <div className="arc-table-wrap" style={{ marginBottom: 12 }}>
          <table className="arc-table">
            <thead><tr><th>Registration</th><th>Rings for inbound calls</th><th /></tr></thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id}>
                  <td className="arc-mono">{r.username}</td>
                  <td>
                    <Switch checked={r.ring_enabled} disabled={!mayRing || act.ring.isPending}
                      label={`${r.username} rings for inbound calls`}
                      onChange={(v) => act.ring.mutate({ id: r.id, ring_enabled: v })} />
                  </td>
                  <td className="arc-td-right">
                    <div className="arc-row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                      {can('extension:credentials:reveal') && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          disabled={act.password.isPending} onClick={() => newPassword(r.id, r.username)}>
                          New password
                        </button>
                      )}
                      {mayWrite && (
                        <RowAction kind="delete" label={`Remove ${r.username}`} disabled={act.remove.isPending}
                          onClick={() => {
                            if (confirm(`Remove ${r.username}? Anything signed in with it stops within a minute.`)) {
                              act.remove.mutate(r.id);
                            }
                          }} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && data && (
        <form onSubmit={add} style={{ maxWidth: 360 }}>
          <div className="arc-field">
            <label className="arc-label" htmlFor="reg-name">Name</label>
            <div className="arc-affix">
              <input id="reg-name" className="arc-input arc-mono" value={name} maxLength={30} autoFocus
                placeholder="luke" aria-invalid={name !== '' && !valid}
                onChange={(e) => setName(e.target.value)} />
              <span className="arc-affix-end arc-mono">-{data.account}</span>
            </div>
            {name && !valid
              ? <div className="arc-error">2 to 30 letters, digits, dots or underscores, starting with a letter.</div>
              : <div className="arc-hint">Signs in as {clean || 'name'}-{data.account}, with its own password.</div>}
          </div>
          <div className="arc-row" style={{ gap: 8 }}>
            <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!valid || act.add.isPending}>
              {act.add.isPending ? 'Adding…' : 'Add'}
            </button>
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => { setAdding(false); setName(''); }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {!adding && list.length === 0 && (
        <p className="arc-subtle" style={{ margin: 0, fontSize: 12.5 }}>
          Up to {data?.max ?? 2} more logins for this extension, each with its own password and ring switch.
        </p>
      )}
      {!adding && full && data && (
        <p className="arc-subtle" style={{ margin: 0, fontSize: 12.5 }}>
          This extension has its {data.max}. Remove one to add another.
        </p>
      )}
      </div>
    </div>
  );
}
